import React, { useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const UserRegisterWeb = () => {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const [msg, setMsg] = useState({ type: "", text: "" });

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);
  const isEmailValid = (email) => emailRegex.test(String(email || "").trim());

  const translateBackend = (data) => {
    if (!data) return t("msg_server_or_network");
    if (data.message_key) return t(data.message_key, data.message_params || {});
    if (data.error_key) return t(data.error_key);
    if (data.error) return t(String(data.error));
    return t("msg_unknown_error");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    const cleanEmail = email.trim();

    if (!cleanEmail) {
      setMsg({ type: "error", text: t("urw_fill_required") });
      return;
    }

    if (!isEmailValid(cleanEmail)) {
      setMsg({ type: "error", text: t("msg_invalid_email") });
      return;
    }

    const dataToSend = {
      email: cleanEmail,
      role: "admin",
    };

    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user_register_web`,
        dataToSend
      );

      if (res.data?.status === "success") {
        const key = res.data?.message_key || "urw_success";
        setMsg({ type: "success", text: t(key) });

        setEmail("");
      } else {
        const key = res.data?.message_key || "urw_failed";
        setMsg({ type: "error", text: t(key) });
      }
    } catch (err) {
      const data = err?.response?.data;
      setMsg({ type: "error", text: translateBackend(data) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gradient-hero min-h-[calc(100vh-150px)]">
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-foreground">{t("urw_title", "Ajouter un administrateur")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("urw_subtitle", "Créer un nouvel administrateur web")}
        </p>

        <div className="mx-auto mt-8 max-w-md">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-app-md">
            {msg.type === "success" ? (
              <div className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-success"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">
                  {t("urw_success_title", "Administrateur ajouté !")}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {msg.text || t("urw_success", "Utilisateur enregistré avec succès.")}
                </p>
                <button
                  onClick={() => setMsg({ type: "", text: "" })}
                  className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                >
                  {t("urw_add_other", "Ajouter un autre")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {msg.type === "error" && msg.text && (
                  <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    {msg.text}
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t("admin_th_email", "Email")} *
                  </label>
                  <input
                    type="email"
                    placeholder={t("urw_ph_email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20"
                    required
                    autoComplete="email"
                    aria-invalid={email.trim() !== "" && !isEmailValid(email)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:bg-primary-hover disabled:opacity-50"
                >
                  {loading ? t("urw_registering") : t("urw_register")}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserRegisterWeb;
